import { Injectable } from "@angular/core";
import { Store } from "@ngxs/store";
import { Observable, Subject } from "rxjs";
import { filter, map, switchMap, take } from "rxjs/operators";
import { isArray, pullAt } from "lodash";

import { SecurePayUITokenizedCard } from "../../../types/secure-pay";
import { UserGetterState } from "../../ngxs/user";
import { IRequestsNestedState } from "../../ngxs/interfaces/requests.interface";
import {
  CheckAccessTokenAction,
  GetAccountInfoAction,
  GetCurrentUserAction,
  LoadMapDataAction,
  LogoutAction,
  RenewUserVehiclesSubscriptionAction,
  SaveAccountInfoAction,
  SaveMapDataAction,
  SetAccessTokenAction,
  SetCurrentUserAction,
  SubscribeToPremiumPlanAction,
} from "../../ngxs/user/user.actions";
import { AccountInfo, MapData, User, Vehicle } from "../models";

@Injectable({
  providedIn: "root",
})
export class UserService {
  currentUser$: Observable<User> = this.store.select(
    UserGetterState.getCurrentUser
  );
  accountInfo$: Observable<AccountInfo> = this.store.select(
    UserGetterState.getAccountInfo
  );
  mapData$: Observable<MapData> = this.store.select(UserGetterState.getMapData);
  accessToken$: Observable<string> = this.store.select(
    UserGetterState.getAccessToken
  );
  currentUserAccessLevel$: Observable<number> = this.store.select(
    UserGetterState.getCurrentUserAccessLevel
  );
  hasCurrentUserPremiumAccess$: Observable<boolean> = this.store.select(
    UserGetterState.hasCurrentUserPremiumAccess
  );
  currentUserPendingVehicles$: Observable<Vehicle[]> = this.store.select(
    UserGetterState.getCurrentUserPendingVehicles
  );
  renewUserVehiclesSubscriptionPutRequestState$: Observable<IRequestsNestedState> =
    this.store.select(
      UserGetterState.getRenewUserVehiclesSubscriptionPutRequestState
    );

  savePolygonEvent$: Subject<{ data: any; name: string }> = new Subject();
  removePolygonEvent$: Subject<number> = new Subject();
  updateMapPolygonNameEvent$: Subject<{ index: number; name: string }> =
    new Subject();

  currentUserRequestState$: Observable<IRequestsNestedState> =
    this.store.select(UserGetterState.getCurrentUserRequestState);
  getAccountInfoRequestState$: Observable<IRequestsNestedState> =
    this.store.select(UserGetterState.getAccountInfoRequestState);
  mapDataRequestState$: Observable<IRequestsNestedState> = this.store.select(
    UserGetterState.getMapDataRequestState
  );
  subscribeToPremiumPlanRequestState$: Observable<IRequestsNestedState> =
    this.store.select(UserGetterState.getSubscribeToPremiumPlanRequestState);

  constructor(private store: Store) {
    this.savePolygonEvent$
      .pipe(
        switchMap(({ data, name }) => {
          return this.mapData$.pipe(
            filter((res) => !!res),
            take(1),
            map((mapData) => {
              const mapJson = {
                type: "FeatureCollection",
                features: isArray(mapData.mapJson?.features)
                  ? [...mapData.mapJson?.features]
                  : [],
              };
              mapJson.features.push({
                type: "Feature",
                geometry: {
                  type: "Polygon",
                  coordinates: [
                    data.map((coord) => {
                      return [
                        typeof coord.lng === "function"
                          ? coord.lng()
                          : coord.lng,
                        typeof coord.lat === "function"
                          ? coord.lat()
                          : coord.lat,
                      ];
                    }),
                  ],
                },
                properties: {
                  GeoFenceName: name,
                },
              });
              return mapJson;
            })
          );
        })
      )
      .subscribe((data) => {
        this.saveMapData(data);
      });

    this.removePolygonEvent$
      .pipe(
        switchMap((index) => {
          return this.mapData$.pipe(
            filter((res) => !!res),
            take(1),
            map((mapData) => {
              const mapJson = {
                type: "FeatureCollection",
                features: isArray(mapData.mapJson?.features)
                  ? [...mapData.mapJson?.features]
                  : [],
              };
              pullAt(mapJson.features, index);
              return mapJson;
            })
          );
        })
      )
      .subscribe((data) => {
        this.saveMapData(data);
      });

    this.updateMapPolygonNameEvent$
      .pipe(
        switchMap(({ index, name }) => {
          return this.mapData$.pipe(
            filter((res) => !!res),
            take(1),
            map((mapData) => {
              const mapJson = {
                type: "FeatureCollection",
                features: isArray(mapData.mapJson?.features)
                  ? [...mapData.mapJson?.features]
                  : [],
              };
              mapJson.features[index] = {
                ...mapJson.features[index],
                properties: { GeoFenceName: name },
              };
              return mapJson;
            })
          );
        })
      )
      .subscribe((data) => {
        this.saveMapData(data);
      });
  }

  getCurrentUser() {
    this.store.dispatch(new GetCurrentUserAction());
  }

  setCurrentUser(user: User) {
    this.store.dispatch(new SetCurrentUserAction(user));
  }

  loadMapData() {
    this.store.dispatch(new LoadMapDataAction());
  }

  saveMapData(data) {
    this.store.dispatch(new SaveMapDataAction(JSON.stringify(data)));
  }

  saveMapPolygon(data, name: string) {
    this.savePolygonEvent$.next({ data, name });
  }

  removeMapPolygon(index: number) {
    this.removePolygonEvent$.next(index);
  }

  updateMapPolygonName(index: number, name: string) {
    this.updateMapPolygonNameEvent$.next({ index, name });
  }

  checkAccessToken() {
    this.store.dispatch(new CheckAccessTokenAction());
  }

  setAccessToken(token: string) {
    this.store.dispatch(new SetAccessTokenAction(token));
  }

  logout() {
    this.store.dispatch(new LogoutAction());
  }

  getAccountInfo() {
    this.store.dispatch(new GetAccountInfoAction());
  }

  saveAccountInfo(updatedAccountInfo: AccountInfo) {
    this.store.dispatch(new SaveAccountInfoAction(updatedAccountInfo));
  }

  subscribeToPremiumPlan() {
    this.store.dispatch(new SubscribeToPremiumPlanAction());
  }

  renewVehiclesSubscription(
    vehicleIds: number[],
    cardData: SecurePayUITokenizedCard
  ) {
    this.store.dispatch(
      new RenewUserVehiclesSubscriptionAction({ vehicleIds, cardData })
    );
  }
}
